import { useParams, Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Check, FileText } from 'lucide-react';
import { api } from '../../lib/api';
import { SiteLayout } from '../../components/site/SiteLayout';
import { PillLink } from '../../components/site/Pill';
import { INTERIORS, schemeImage } from '../../lib/site';
import { Reveal } from '../../components/site/Reveal';
import { Lightbox, type LightboxItem } from '../../components/site/Lightbox';
import { ImageCarousel } from '../../components/site/ImageCarousel';
import { formatRent, bedsLabel, ListingCard, type Listing, type ListingDoc, type ListingPhoto } from './ListingCard';

const FURNISHED: Record<string, string> = { FURNISHED: 'Furnished', UNFURNISHED: 'Unfurnished', PART_FURNISHED: 'Part furnished', FLEXIBLE: 'Furnished or not' };
const LET: Record<string, string> = { LONG_TERM: 'Long let', SHORT_TERM: 'Short let', STUDENT: 'Student let', COMMERCIAL: 'Commercial' };
const OUTSIDE: Record<string, string> = { BALCONY: 'Balcony', TERRACE: 'Terrace', PATIO: 'Patio', GARDEN: 'Garden', COMMUNAL: 'Communal garden', ROOF: 'Roof terrace' };
const PARKING: Record<string, string> = { ALLOCATED: 'Allocated parking', UNDERGROUND: 'Underground parking', GARAGE: 'Garage', PERMIT: 'Permit parking', VISITOR: 'Visitor parking' };

export default function SiteListingDetail() {
  const { slug } = useParams<{ slug: string }>();
  // The old "?book" link (the viewing form used to open on this page) goes to the viewing page, keeping any other parameters.
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const bookLink = params.has('book');
  params.delete('book');
  // The phone/tablet bottom bar shows only while the booking card is off screen, so it never doubles up or hides it.
  const card = useRef<HTMLDivElement>(null);
  const [cardInView, setCardInView] = useState(true);
  // The on-page pop-up: which group of pictures / PDFs is open, and at which one.
  const [viewer, setViewer] = useState<{ items: LightboxItem[]; index: number; title?: string } | null>(null);
  const openViewer = (items: LightboxItem[], index = 0, title?: string) => setViewer({ items, index, title });
  const { data: l, isLoading, isError } = useQuery({ queryKey: ['public-listing', slug], queryFn: async () => (await api.public.get<Listing>(`/public/listings/${slug}`)).data, enabled: !!slug });
  const nearby = useQuery({
    queryKey: ['public-listings', 'nearby', l?.property?.city],
    queryFn: async () => (await api.public.get<Listing[]>('/public/listings', { city: l?.property?.city, perPage: 4 })).data,
    enabled: !!l?.property?.city,
  });

  useEffect(() => {
    if (bookLink) return;
    const el = card.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => setCardInView(!!e?.isIntersecting), { rootMargin: '0px 0px -80px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [l?.id]);

  if (bookLink) return <Navigate to={`/rent/${slug}/viewing${params.toString() ? `?${params.toString()}` : ''}`} replace />;
  if (isLoading) return <SiteLayout><div className="site-container py-40 text-center text-ink-400">Loading…</div></SiteLayout>;
  if (isError || !l) {
    return (
      <SiteLayout>
        <div className="site-container py-28 text-center sm:py-40">
          <p className="h-section text-ink-900">This home has just been let.</p>
          <p className="mt-3 text-ink-500">Homes move quickly. Here is what is still available.</p>
          <PillLink to="/rent" className="mt-8">See available homes</PillLink>
        </div>
      </SiteLayout>
    );
  }

  const rent = formatRent(l.advertisedRentMinor, l.advertisedRentCurrency);
  const period = l.rentPeriod === 'YEAR' ? 'year' : 'month';
  const photos = l.photos ?? [];
  const plans = l.floorplans?.length ? l.floorplans : l.floorplan ? [l.floorplan] : [];
  const hero = l.media?.[0] ?? schemeImage(l.property?.slug);
  const photoItems = photos.map((p): LightboxItem => ({ src: p.url, kind: 'image', alt: photoAlt(p, l.property?.name), label: p.label, caption: p.caption }));
  const mediaItems = (l.media ?? []).map((src): LightboxItem => ({ src, kind: 'image', alt: l.property?.name }));
  const furnished = l.furnished ? FURNISHED[l.furnished] ?? l.furnished : l.unit?.isFurnished ? 'Furnished' : 'Unfurnished';
  // Key features: the flat's basics first (size, furnishing, EPC, bills, pets, when it is free), then the advert's own list.
  const basics = [
    bedsLabel(l.unit?.bedrooms),
    `${l.unit?.bathrooms ?? 1} bath`,
    l.unit?.areaSqft ? `${l.unit.areaSqft} sq ft` : null,
    furnished,
    l.epcRating ? `EPC ${l.epcRating}${l.epcCurrent ? ` (${l.epcCurrent})` : ''}` : null,
    l.billsIncluded ? `Bills included${l.billsDetail ? `: ${l.billsDetail.toLowerCase()}` : ''}` : null,
    l.petsAllowed === true ? 'Pets considered' : null,
  ].filter((x): x is string => !!x);
  const keyFeatures = [...basics, ...(l.features?.length ? l.features.slice(0, 10) : l.unit?.features ?? [])];
  // The first day it can be moved into (the API's availableFrom: the advert's date, or the current tenancy's end if later).
  const available = l.availableFrom && new Date(l.availableFrom) > new Date() ? `from ${new Date(l.availableFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : 'Now';
  keyFeatures.splice(basics.length, 0, `Available ${available.toLowerCase() === 'now' ? 'now' : available}`);
  const others = (nearby.data ?? []).filter((n) => n.id !== l.id).slice(0, 3);

  const r = (i: number) => ({ '--i': i }) as CSSProperties;

  return (
    <SiteLayout>
      <div className="site-container pt-5 lg:pt-7">
        {/* Gallery — the flat's photographs in the order staff set (the first is the cover), each labelled with what it
            shows ("Bedroom 2"), moving on by themselves; else the advert's images; else the scheme's with stock fillers.
            Tapping a photo opens the pop-up. */}
        {photos.length || l.media?.length ? (
          <div className="rise overflow-hidden rounded-[10px] bg-ink-100" style={r(1)}>
            {photos.length
              ? <ImageCarousel images={photos.map((ph) => ({ src: ph.url, alt: photoAlt(ph, l.property?.name), label: ph.label ?? undefined }))} className="sm:aspect-[16/9] lg:aspect-[21/9]" every={5000} onOpen={(i) => openViewer(photoItems, i, 'Photos')} />
              : <ImageCarousel images={l.media!} alt={l.property?.name} className="sm:aspect-[16/9] lg:aspect-[21/9]" every={5000} onOpen={(i) => openViewer(mediaItems, i, 'Photos')} />}
          </div>
        ) : (
          // No pictures of the flat at all: the scheme's image with two stock interiors beside it (decoration only).
          <div className="rise grid gap-3 lg:grid-cols-4 lg:grid-rows-2" style={r(1)}>
            <div className="overflow-hidden rounded-[10px] bg-ink-100 lg:col-span-3 lg:row-span-2">
              <img src={hero} alt={l.property?.name} className="aspect-[4/3] h-full w-full object-cover sm:aspect-[16/10]" />
            </div>
            <div className="hidden overflow-hidden rounded-[10px] bg-ink-100 lg:block"><img src={INTERIORS.livingArea} alt="" className="h-full w-full object-cover" loading="lazy" /></div>
            <div className="hidden overflow-hidden rounded-[10px] bg-ink-100 lg:block"><img src={l.unit?.bedrooms === 0 ? INTERIORS.kitchenDetail : INTERIORS.bedroom} alt="" className="h-full w-full object-cover" loading="lazy" /></div>
          </div>
        )}

        <div className="mt-8 grid gap-10 sm:mt-12 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-16 xl:gap-24">
          <div className="min-w-0">
            <p className="rise eyebrow" style={r(2)}>{l.property?.name} · {l.property?.city}</p>
            <h1 className="rise h-section mt-3 text-ink-900 sm:mt-4" style={r(3)}>{l.headline}</h1>
            <p className="rise mt-3 flex items-baseline gap-2 lg:hidden" style={r(3)}><span className="h-card text-ink-900">{rent}</span><span className="text-[14px] text-ink-400">per {period}</span></p>


            {l.description && <Reveal as="p" className="prose-site mt-6 max-w-[68ch] whitespace-pre-line text-ink-600 sm:mt-8">{l.description}</Reveal>}

            {!!keyFeatures.length && (
              <Reveal as="section" className="mt-10 sm:mt-12">
                <h2 className="h-card text-ink-900">Key features</h2>
                <ul className="mt-4 grid gap-x-6 gap-y-2.5 sm:grid-cols-2">{keyFeatures.map((a) => <li key={a} className="flex items-start gap-2.5 text-[15px] text-ink-700"><Check className="mt-1 h-4 w-4 shrink-0 text-bronze-500" />{a}</li>)}</ul>
              </Reveal>
            )}
            {(plans.length > 0 || l.epcGraph) && (
              <Reveal as="section" className="mt-10 grid gap-8 sm:grid-cols-2 sm:gap-6">
                {plans.length > 0 && (
                  <div>
                    <h2 className="h-card text-ink-900">{plans.length > 1 ? 'Floor plans' : 'Floor plan'}</h2>
                    {plans.map((f, i) => <DocTile key={f.url} doc={f} name="floor plan" onOpen={() => openViewer(plans.map((d) => docItem(d, 'Floor plan')), i, plans.length > 1 ? 'Floor plans' : 'Floor plan')} />)}
                  </div>
                )}
                {l.epcGraph && (
                  <div>
                    <h2 className="h-card text-ink-900">Energy performance</h2>
                    <DocTile doc={l.epcGraph} name="EPC" onOpen={() => openViewer([docItem(l.epcGraph!, 'EPC graph')], 0, 'Energy performance')} />
                  </div>
                )}
              </Reveal>
            )}
            {(l.virtualTourUrl || l.videoUrl) && <p className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-[14px]">{l.virtualTourUrl && <a href={l.virtualTourUrl} target="_blank" rel="noreferrer" className="copy-link font-medium">Virtual tour</a>}{l.videoUrl && <a href={l.videoUrl} target="_blank" rel="noreferrer" className="copy-link font-medium">Video</a>}</p>}
            {!!l.property?.amenities?.length && (
              <Reveal as="section" className="mt-10">
                <h2 className="h-card text-ink-900">In the building</h2>
                <ul className="mt-4 grid gap-x-6 gap-y-2.5 sm:grid-cols-2">{l.property.amenities.map((a) => <li key={a} className="flex items-start gap-2.5 text-[15px] text-ink-700"><Check className="mt-1 h-4 w-4 shrink-0 text-bronze-500" />{a}</li>)}</ul>
              </Reveal>
            )}

          </div>

          <aside className="min-w-0 lg:sticky lg:top-28 lg:self-start">
            <div ref={card} className="rise surface scroll-mt-24 p-5 sm:p-7" style={r(4)}>
              <div className="flex items-baseline gap-2">
                <span className="h-stat text-ink-900">{rent}</span>
                <span className="text-[14px] text-ink-400">per {period}</span>
              </div>
              <dl className="mt-5 divide-y divide-ink-100 text-[14px] sm:mt-6">
                <Row k="Available" v={available === 'Now' ? 'Now' : `From ${available.slice(5)}`} />
                {!!l.depositMinor && <Row k="Deposit" v={formatRent(l.depositMinor, l.depositCurrency ?? l.advertisedRentCurrency)} />}
                {!!l.holdingDepositMinor && <Row k="Holding deposit" v={formatRent(l.holdingDepositMinor, l.advertisedRentCurrency)} />}
                <Row k="Furnished" v={furnished} />
                {l.letType && <Row k="Kind of let" v={LET[l.letType] ?? l.letType} />}
                <Row k="Bills" v={l.billsIncluded ? `Included${l.billsDetail ? ` (${l.billsDetail})` : ''}` : l.billsIncluded === false ? 'Not included' : 'Ask us'} />
                {l.councilTaxBand && <Row k="Council tax" v={`Band ${l.councilTaxBand}`} />}
                {l.epcRating && <Row k="EPC" v={`${l.epcRating}${l.epcCurrent ? ` · ${l.epcCurrent}${l.epcPotential ? ` → ${l.epcPotential}` : ''}` : ''}`} />}
                {l.outsideSpace && <Row k="Outside" v={OUTSIDE[l.outsideSpace] ?? l.outsideSpace} />}
                {l.parking && <Row k="Parking" v={PARKING[l.parking] ?? l.parking} />}
                <Row k="Pets" v={l.petsAllowed === true ? 'Considered' : l.petsAllowed === false ? 'No' : 'Ask us'} />
                <Row k="Tenancy" v={l.property?.region === 'AE' ? 'Annual · Ejari registered' : 'Rolling periodic'} />
              </dl>
              {/* One way on: book a viewing (/viewing). The application comes after the viewing, from their account (2 Nov 2026). */}
              <div className="mt-6 flex justify-center sm:mt-7">
                <PillLink small to={`/rent/${l.slug}/viewing`} className="gap-3 whitespace-nowrap px-7 py-3.5">Book a viewing</PillLink>
              </div>
            </div>
          </aside>
        </div>

        {!!others.length && (
          <section className="section-b mt-16 border-t border-ink-100 pt-12 sm:mt-24 sm:pt-16">
            <Reveal as="h2" className="h-section text-ink-900">More in {l.property?.city}</Reveal>
            <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-10 sm:mt-10 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-8">{others.map((n, i) => <Reveal key={n.id} className="min-w-0" delay={(i % 3) * 110}><ListingCard listing={n} index={i + 1} /></Reveal>)}</div>
          </section>
        )}
        {!others.length && <div className="section-b" />}
      </div>

      {/* Phones and tablets: price and Book a viewing, pinned to the bottom while the booking card is off screen. */}
      <div className="listing-cta-bar lg:hidden" data-show={!cardInView}>
        <div className="flex items-center gap-3 px-4 py-3 sm:px-10">
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate"><span className="font-display text-[22px] font-medium text-ink-900">{rent}</span> <span className="text-[12px] text-ink-500">per {period}</span></div>
            <div className="truncate text-[12px] text-ink-500">{available === 'Now' ? 'Available now' : `Available ${available}`}</div>
          </div>
          <PillLink small to={`/rent/${l.slug}/viewing`} className="!min-h-[44px] gap-2 whitespace-nowrap px-4">Book a viewing</PillLink>
        </div>
      </div>
      {viewer && <Lightbox items={viewer.items} index={viewer.index} title={viewer.title} onClose={() => setViewer(null)} />}
    </SiteLayout>
  );
}

/** A floor plan or EPC: an image shows inline and opens in the pop-up; a PDF is a button that opens it there. */
function DocTile({ doc, name, onOpen }: { doc: ListingDoc; name: string; onOpen: () => void }) {
  const image = doc.mimeType.startsWith('image/');
  return (
    <figure>
      {image ? (
        <button type="button" onClick={onOpen} aria-label={`Open the ${name} full screen`} className="site-zoomable mt-3 block w-full rounded-[10px]">
          <img src={doc.url} alt={doc.caption ?? (name === 'EPC' ? 'EPC graph' : 'Floor plan')} className="w-full rounded-[10px] border border-ink-100 bg-white" loading="lazy" />
        </button>
      ) : (
        <button type="button" onClick={onOpen} className="site-zoomable mt-3 inline-flex min-h-[44px] items-center gap-2 rounded-md text-bronze-600"><FileText className="h-4 w-4" />View the {name} (PDF)</button>
      )}
      {doc.caption && <figcaption className="mt-1.5 text-[12px] text-ink-400">{doc.caption}</figcaption>}
    </figure>
  );
}

/** A floor plan / EPC as a pop-up item: PDFs get the PDF viewer and a Download link. */
function docItem(d: ListingDoc, fallback: string): LightboxItem {
  const image = d.mimeType.startsWith('image/');
  return { src: d.url, kind: image ? 'image' : 'pdf', alt: d.caption ?? fallback, label: d.label ?? fallback, caption: d.caption, downloadUrl: image ? undefined : d.url };
}

const Row = ({ k, v }: { k: string; v: string }) => (
  <div className="flex justify-between gap-4 py-2.5"><dt className="shrink-0 text-ink-500">{k}</dt><dd className="min-w-0 text-right font-medium text-ink-900">{v}</dd></div>
);

/** A photo's alt text: what it shows and its caption, else the building's name. */
function photoAlt(p: ListingPhoto, building?: string): string {
  return [p.label, p.caption].filter(Boolean).join(' — ') || building || '';
}
